// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import React, { useState, useEffect, useCallback, useRef } from "react"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormLabel,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import type { Theme } from "@mui/material/styles"
import { useToast } from "@crm-src/hooks/use-toast"
import { customerActivitiesApi, type CustomerActivity, type ScheduleActivityData } from "@crm-src/lib/customer-activities-api"
import { SearchInput } from "./search-input"
import { useUsers, type User } from "@crm-src/hooks/useUsers"
import { useTasks } from "@crm-src/hooks/useTasks"
import { DatePicker } from "@/components/ui/date-picker"
import { TimePicker } from "@/components/ui/time-picker"
import {
  Activity as ActivityIcon,
  Plus,
  Phone,
  MoreHorizontal,
  Edit,
  Trash2,
  Search,
} from "lucide-react"
import { getActivityIcon } from "./activity-log/activity-icon"
import { formatActivityDate } from "./activity-log/format"

interface ActivityLogPanelProps {
  customerId: string
  customerName?: string
  onEdit?: (activityId: string) => void
  onDelete?: (activityId: string) => void
  title?: string
  className?: string
  showScheduleButton?: boolean
  autoRefresh?: boolean
}

export function ActivityLogPanel({
  customerId,
  customerName,
  onEdit,
  onDelete,
  title = "Activity Log",
  className = "",
  showScheduleButton = true,
  autoRefresh = true,
}: ActivityLogPanelProps) {
  const { toast } = useToast()
  const { users, loading: isLoadingUsers, refresh: getUsers } = useUsers()
  const { createTask } = useTasks()

  // State management
  const [activities, setActivities] = useState<CustomerActivity[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isScheduling, setIsScheduling] = useState(false)

  // Per-row actions menu anchor (keyed by activity id) — replaces the shadcn
  // DropdownMenu during the MUI 5 migration.
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; id: string } | null>(null)

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 5
  const [totalActivities, setTotalActivities] = useState(0)

  // Search state
  const [searchQuery, setSearchQuery] = useState("")

  // Schedule activity form state
  const today = new Date()
  const [scheduleForm, setScheduleForm] = useState<ScheduleActivityData>({
    title: "",
    description: "",
    activity_type: "call",
    due_date: new Date().toISOString().slice(0, 16), // datetime-local format
    task_priority: "medium",
    assigned_to_id: undefined, // Unassigned by default
    duration_minutes: 30,
    location: "",
    status: "scheduled"
  })

  // Separate date and time for pickers
  const [activityDate, setActivityDate] = useState(today.toISOString().split('T')[0]) // yyyy-MM-dd
  const [activityTime, setActivityTime] = useState(today.toTimeString().slice(0, 5)) // HH:mm

  // Track if this is the initial load (for showing loading spinner)
  const isInitialLoadRef = useRef(true)

  // Load activities with server-side pagination and search
  const loadActivities = useCallback(async () => {
    if (!customerId) {
      return
    }

    try {
      // Only show loading spinner on initial load, not on search/pagination
      if (isInitialLoadRef.current) {
        setIsLoading(true)
      }
      const response = await customerActivitiesApi.getActivities(customerId, {
        page: currentPage,
        per_page: itemsPerPage,
        search: searchQuery || undefined
      })
      const fetchedActivities = response.activities || []
      const totalCount = response.total_activities || 0

      setActivities(fetchedActivities)
      setTotalActivities(totalCount)
      isInitialLoadRef.current = false

      // If current page is empty but there are items on previous pages, go back
      if (fetchedActivities.length === 0 && totalCount > 0 && currentPage > 1) {
        setCurrentPage(currentPage - 1)
      }
    } catch (error: any) {
      // Ignore cancelled requests (common in React Strict Mode)
      const isCancelled = error.code === 'ERR_NETWORK_CHANGED' ||
                         error.code === 'ERR_CANCELED' ||
                         (error.message === 'Network Error' && !error.response && !error.request?.status);

      if (isCancelled) {
        // Silently ignore cancelled requests
        setIsLoading(false)
        return
      }

      console.error('Failed to load customer activities:', error)

      // Extract error message from response
      let errorMessage = "Failed to load activity history"
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.message) {
        errorMessage = error.message
      }

      // Log detailed error for debugging
      if (error?.response) {
        console.error('API Error Details:', {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
          url: error.config?.url
        })
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }, [customerId, currentPage, searchQuery, toast])

  // Refresh activities
  const refreshActivities = useCallback(async () => {
    if (!customerId || isLoading) return

    try {
      setIsRefreshing(true)
      const response = await customerActivitiesApi.getActivities(customerId, {
        page: currentPage,
        per_page: itemsPerPage,
        search: searchQuery || undefined
      })
      setActivities(response.activities || [])
      setTotalActivities(response.total_activities || 0)
      toast({
        title: "Refreshed",
        description: "Activity log updated",
      })
    } catch (error: any) {
      // Ignore cancelled requests (common in React Strict Mode)
      const isCancelled = error.code === 'ERR_NETWORK_CHANGED' ||
                         error.code === 'ERR_CANCELED' ||
                         (error.message === 'Network Error' && !error.response && !error.request?.status);

      if (isCancelled) {
        setIsRefreshing(false)
        return
      }

      console.error('Failed to refresh activities:', error)

      // Extract error message from response
      let errorMessage = "Failed to refresh activity log"
      if (error?.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error?.message) {
        errorMessage = error.message
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
    } finally {
      setIsRefreshing(false)
    }
  }, [customerId, isLoading, currentPage, searchQuery, toast])

  // Schedule activity (now creates a task in the new tasks system)
  const handleScheduleActivity = async () => {
    if (!customerId || !scheduleForm.title.trim() || isScheduling) return

    setIsScheduling(true)
    try {
      // Convert datetime-local format to ISO 8601
      const dueDateTime = new Date(scheduleForm.due_date).toISOString()

      // Create task in the new tasks system
      await createTask({
        name: scheduleForm.title,
        description: scheduleForm.description,
        priority: scheduleForm.task_priority as 'low' | 'medium' | 'high' | 'urgent',
        status: 'pending',
        due_date: dueDateTime,
        assigned_to_id: scheduleForm.assigned_to_id,
        customer_id: parseInt(customerId),
        estimated_duration_minutes: scheduleForm.duration_minutes,
        metadata: {
          activity_type: scheduleForm.activity_type,
          location: scheduleForm.location,
        }
      })

      toast({
        title: "Success",
        description: `Task scheduled successfully`,
      })

      setIsScheduleDialogOpen(false)
      setScheduleForm({
        title: "",
        description: "",
        activity_type: "call",
        due_date: new Date().toISOString().slice(0, 16),
        task_priority: "medium",
        assigned_to_id: undefined,
        duration_minutes: 30,
        location: "",
        status: "scheduled"
      })

      // Refresh activities to show the new task (activities will show tasks too)
      loadActivities()

      // Notify tasks page to refresh
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('refreshTasks', {
          detail: { message: 'Task scheduled successfully' }
        }))
      }
    } catch (error: any) {
      console.error('Failed to schedule task:', error)
      toast({
        title: "Error",
        description: error.message || "Failed to schedule task",
        variant: "destructive",
      })
    } finally {
      setIsScheduling(false)
    }
  }

  // Load activities on mount and when customerId changes
  useEffect(() => {
    loadActivities()
  }, [loadActivities])

  // Search handlers passed to memoized SearchInput
  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
  }, [])

  const handleClearSearch = useCallback(() => {
    setSearchQuery("")
    setCurrentPage(1)
  }, [])

  // Auto-refresh every 30 seconds if enabled
  useEffect(() => {
    if (!autoRefresh) return

    const interval = setInterval(() => {
      if (!isLoading && !isRefreshing) {
        loadActivities()
      }
    }, 30000)

    return () => clearInterval(interval)
  }, [autoRefresh, isLoading, isRefreshing, loadActivities])

  // Load users when schedule dialog opens
  useEffect(() => {
    if (isScheduleDialogOpen && users.length === 0) {
      getUsers()
    }
  }, [isScheduleDialogOpen, users.length, getUsers])

  // Reset form when dialog opens (using ref to track if it's the first open)
  const dialogOpenedRef = useRef(false)
  useEffect(() => {
    if (isScheduleDialogOpen && !dialogOpenedRef.current) {
      dialogOpenedRef.current = true
      const today = new Date()
      setScheduleForm({
        title: "",
        description: "",
        activity_type: "call",
        due_date: today.toISOString().slice(0, 16),
        task_priority: "medium",
        assigned_to_id: undefined,
        duration_minutes: 30,
        location: "",
        status: "scheduled"
      })
      setActivityDate(today.toISOString().split('T')[0])
      setActivityTime(today.toTimeString().slice(0, 5))
    } else if (!isScheduleDialogOpen) {
      dialogOpenedRef.current = false
    }
  }, [isScheduleDialogOpen])

  // Inline status/priority pill styling — replaces the former Tailwind
  // status-class badges during the MUI 5 migration. Each family maps to a
  // concrete MUI palette ref (tinted bg + dark text); 'neutral' is the
  // gray fallback.
  type PillFamily = 'success' | 'warning' | 'error' | 'info' | 'neutral'
  const pillSx = (family: PillFamily) => {
    const base = { fontSize: 12, px: 1, py: 0.5, borderRadius: 999 }
    switch (family) {
      case 'success':
        return { ...base, bgcolor: (t: Theme) => alpha(t.palette.success.main, 0.12), color: "success.dark" }
      case 'warning':
        return { ...base, bgcolor: (t: Theme) => alpha(t.palette.warning.main, 0.12), color: "warning.dark" }
      case 'error':
        return { ...base, bgcolor: (t: Theme) => alpha(t.palette.error.main, 0.12), color: "error.dark" }
      case 'info':
        return { ...base, bgcolor: (t: Theme) => alpha(t.palette.info.main, 0.12), color: "info.dark" }
      default:
        return { ...base, bgcolor: "action.hover", color: "text.secondary" }
    }
  }

  // Map a priority value to a pill family.
  const priorityFamily = (priority: string): PillFamily =>
    priority === 'urgent' ? 'error' : priority === 'high' || priority === 'medium' ? 'warning' : 'neutral'

  // Map a status value to a pill family.
  const statusFamily = (status: string): PillFamily =>
    status === 'completed' ? 'success' : status === 'scheduled' ? 'info' : status === 'in_progress' ? 'warning' : 'neutral'




  if (isLoading) {
    return (
      <Card {...(className ? { className } : {})}>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ActivityIcon size={20} />
              <Typography component="span">{title}</Typography>
            </Box>
          }
        />
        <CardContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" sx={{ height: 48 }} />
            ))}
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card {...(className ? { className } : {})}>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ActivityIcon size={20} />
                <Typography component="span">{title}</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {showScheduleButton && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    startIcon={<Plus size={16} />}
                    onClick={() => setIsScheduleDialogOpen(true)}
                  >
                    Schedule Activity
                  </Button>
                )}
              </Box>
            </Box>
          }
        />
        <CardContent>
          {/* Memoized Search Input - isolated from parent re-renders */}
          <SearchInput
            placeholder="Search activities..."
            onSearch={handleSearch}
            onClear={handleClearSearch}
            searchQuery={searchQuery}
          />

          {isLoading ? (
            <Box sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1, color: "text.disabled" }}><ActivityIcon size={32} /></Box>
              <Typography sx={{ fontSize: 14 }}>Loading...</Typography>
            </Box>
          ) : activities.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 2, color: "text.secondary" }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1, color: "text.disabled" }}><ActivityIcon size={32} /></Box>
              <Typography sx={{ fontSize: 14 }}>{searchQuery ? `No activities found matching "${searchQuery}"` : 'No activities recorded yet'}</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {activities.map((activity) => (
                <Box key={activity.id} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}>
                  <Box sx={{ mt: 0.25, display: "flex" }}>
                    {getActivityIcon(activity.type)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{activity.description}</Typography>
                      {activity.details?.priority && (
                        <Typography component="span" sx={pillSx(priorityFamily(activity.details.priority))}>
                          {activity.details.priority}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1, rowGap: 0.5, mt: 1 }}>
                      <Typography component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
                        {formatActivityDate(activity.date)}
                      </Typography>

                      {activity.author && (
                        <>
                          <Typography component="span" sx={{ fontSize: 12, color: "text.disabled" }}>•</Typography>
                          <Typography component="span" sx={{ fontSize: 12, color: "text.secondary" }}>{activity.author}</Typography>
                        </>
                      )}

                      {activity.assignee && activity.assignee !== activity.author && (
                        <>
                          <Typography component="span" sx={{ fontSize: 12, color: "text.disabled" }}>•</Typography>
                          <Typography component="span" sx={{ fontSize: 12, color: "text.secondary" }}>Assigned to: {activity.assignee}</Typography>
                        </>
                      )}

                      {activity.details?.status && (
                        <>
                          <Typography component="span" sx={{ fontSize: 12, color: "text.disabled" }}>•</Typography>
                          <Typography component="span" sx={pillSx(statusFamily(activity.details.status))}>
                            {activity.details.status.replace('_', ' ')}
                          </Typography>
                        </>
                      )}

                      {activity.details?.overdue && (
                        <>
                          <Typography component="span" sx={{ fontSize: 12, color: "text.disabled" }}>•</Typography>
                          <Typography component="span" sx={pillSx('error')}>
                            overdue
                          </Typography>
                        </>
                      )}

                      {activity.details?.duration && (
                        <>
                          <Typography component="span" sx={{ fontSize: 12, color: "text.disabled" }}>•</Typography>
                          <Typography component="span" sx={{ fontSize: 12, color: "text.secondary" }}>{activity.details.duration}</Typography>
                        </>
                      )}

                      {activity.details?.result && (
                        <>
                          <Typography component="span" sx={{ fontSize: 12, color: "text.disabled" }}>•</Typography>
                          <Typography component="span" sx={{ fontSize: 12, color: "text.secondary" }}>{activity.details.result}</Typography>
                        </>
                      )}

                      {activity.details?.opened && (
                        <>
                          <Typography component="span" sx={{ fontSize: 12, color: "text.disabled" }}>•</Typography>
                          <Typography component="span" sx={pillSx('success')}>
                            opened
                          </Typography>
                        </>
                      )}

                      {activity.details?.replied && (
                        <>
                          <Typography component="span" sx={{ fontSize: 12, color: "text.disabled" }}>•</Typography>
                          <Typography component="span" sx={pillSx('info')}>
                            replied
                          </Typography>
                        </>
                      )}

                      {activity.details?.location && (
                        <>
                          <Typography component="span" sx={{ fontSize: 12, color: "text.disabled" }}>•</Typography>
                          <Typography component="span" sx={{ fontSize: 12, color: "text.secondary" }}>📍 {activity.details.location}</Typography>
                        </>
                      )}

                      {activity.details?.due_date && activity.type === 'task' && (
                        <>
                          <Typography component="span" sx={{ fontSize: 12, color: "text.disabled" }}>•</Typography>
                          <Typography component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
                            Due: {new Date(activity.details.due_date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit'
                            })}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>

                  {(onEdit || onDelete) && (
                    <>
                      <IconButton
                        size="small"
                        sx={{ width: 32, height: 32 }}
                        aria-label="Open menu"
                        onClick={(e) => setMenuAnchor({ el: e.currentTarget, id: activity.id })}
                      >
                        <MoreHorizontal size={16} />
                      </IconButton>
                      <Menu
                        anchorEl={menuAnchor?.id === activity.id ? menuAnchor.el : null}
                        open={menuAnchor?.id === activity.id}
                        onClose={() => setMenuAnchor(null)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                      >
                        <ListSubheader sx={{ lineHeight: 2, bgcolor: "transparent" }}>Activity Actions</ListSubheader>
                        {onEdit && (
                          <MenuItem onClick={() => { setMenuAnchor(null); onEdit(activity.id); }}>
                            <Box sx={{ mr: 1, display: "flex" }}><Edit size={16} /></Box>
                            Edit
                          </MenuItem>
                        )}
                        {onDelete && (
                          <>
                            <Divider />
                            <MenuItem
                              onClick={() => { setMenuAnchor(null); onDelete(activity.id); }}
                              sx={{ color: "error.main" }}
                            >
                              <Box sx={{ mr: 1, display: "flex" }}><Trash2 size={16} /></Box>
                              Delete
                            </MenuItem>
                          </>
                        )}
                      </Menu>
                    </>
                  )}
                </Box>
              ))}
            </Box>

            {/* Pagination Controls */}
            {totalActivities > itemsPerPage && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <Typography component="span" sx={{ fontSize: 14, color: "text.secondary" }}>
                    {currentPage} / {Math.ceil(totalActivities / itemsPerPage)}
                  </Typography>
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={() => setCurrentPage(prev => Math.min(Math.ceil(totalActivities / itemsPerPage), prev + 1))}
                    disabled={currentPage >= Math.ceil(totalActivities / itemsPerPage)}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}
          </>
          )}
        </CardContent>
      </Card>

      {/* Schedule Activity Dialog */}
      <Dialog open={isScheduleDialogOpen} onClose={() => setIsScheduleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Schedule Activity
          <Typography sx={{ fontSize: 14, color: "text.secondary", mt: 0.5 }}>
            Create a new task for {customerName || 'this customer'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <FormLabel htmlFor="activity_type">Activity Type</FormLabel>
                <Select
                  id="activity_type"
                  size="small"
                  value={scheduleForm.activity_type}
                  onChange={(e) => setScheduleForm(prev => ({ ...prev, activity_type: e.target.value as any }))}
                >
                  <MenuItem value="call">Phone Call</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="meeting">Meeting</MenuItem>
                  <MenuItem value="demo">Demo</MenuItem>
                  <MenuItem value="follow_up">Follow Up</MenuItem>
                  <MenuItem value="deal_renewal">Deal Renewal</MenuItem>
                  <MenuItem value="task">General Task</MenuItem>
                  <MenuItem value="note">Note</MenuItem>
                </Select>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <FormLabel htmlFor="task_priority">Priority</FormLabel>
                <Select
                  id="task_priority"
                  size="small"
                  value={scheduleForm.task_priority ?? "medium"}
                  onChange={(e) => setScheduleForm(prev => ({ ...prev, task_priority: e.target.value as any }))}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <FormLabel htmlFor="assigned_to">Assign To</FormLabel>
              <Select
                id="assigned_to"
                size="small"
                displayEmpty
                value={scheduleForm.assigned_to_id?.toString() || "unassigned"}
                onChange={(e) => setScheduleForm(prev => ({
                  ...prev,
                  assigned_to_id: e.target.value === "unassigned" ? undefined : parseInt(e.target.value)
                }))}
                renderValue={(value) => {
                  if (value === "unassigned") {
                    return <Typography component="span" sx={{ color: "text.disabled" }}>{isLoadingUsers ? "Loading users..." : "Select user (optional)"}</Typography>
                  }
                  const user = users.find(u => u.id.toString() === value)
                  return user ? `${user.full_name} (${user.email})` : value
                }}
              >
                <MenuItem value="unassigned">Unassigned</MenuItem>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id.toString()}>
                    {user.full_name} ({user.email})
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <FormLabel htmlFor="title">Title</FormLabel>
              <TextField
                id="title"
                size="small"
                fullWidth
                value={scheduleForm.title}
                onChange={(e) => setScheduleForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Enter activity title..."
                required
              />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <FormLabel htmlFor="description">Description</FormLabel>
              <TextField
                id="description"
                size="small"
                fullWidth
                multiline
                rows={3}
                value={scheduleForm.description}
                onChange={(e) => setScheduleForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description..."
              />
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <FormLabel>Date *</FormLabel>
                <DatePicker
                  value={activityDate ?? ""}
                  onChange={(date) => {
                    setActivityDate(date)
                    // Update the datetime-local format
                    setScheduleForm(prev => ({ ...prev, due_date: `${date}T${activityTime}` }))
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <FormLabel>Time *</FormLabel>
                <TimePicker
                  value={activityTime}
                  onChange={(time) => {
                    setActivityTime(time)
                    // Update the datetime-local format
                    setScheduleForm(prev => ({ ...prev, due_date: `${activityDate}T${time}` }))
                  }}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <FormLabel htmlFor="duration_minutes">Duration (min)</FormLabel>
                <TextField
                  id="duration_minutes"
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{ min: "1", max: "1440" }}
                  value={scheduleForm.duration_minutes || ''}
                  onChange={(e) => setScheduleForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || undefined }))}
                  placeholder="30"
                />
              </Box>
            </Box>

            {(scheduleForm.activity_type === 'meeting' || scheduleForm.activity_type === 'demo') && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <FormLabel htmlFor="location">Location</FormLabel>
                <TextField
                  id="location"
                  size="small"
                  fullWidth
                  value={scheduleForm.location}
                  onChange={(e) => setScheduleForm(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="Meeting location or video link..."
                />
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 2 }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setIsScheduleDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleScheduleActivity}
              disabled={!scheduleForm.title || scheduleForm.title.trim() === '' || isScheduling}
            >
              {isScheduling ? "Scheduling..." : "Schedule Activity"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
}
